import Link from 'next/link';
import React from 'react';
import { useCityBuilder } from '../../hooks/useCityBuilder';
import Alert from '../Alert';
import Button from '../Button';
import CityImage from './CityImage';
import FullScreenLoaderConfirm from '../FullScreenLoaderConfirm';
import Spinner from '../Spinner';
import formatNumber from '../../utils/formatNumber';

const CityBuilderCityConfirm = ({ allCities }) => {
  const {
    blocksInCity,
    originalBlocksInCity,
    confirmCity,
    initiateConfirmCity,
    txnsToConfirm,
    selectedCity,
    confirmLoading,
    resetFlow,
    hasClickedConfirm,
    originalUpgradeUrl,
    currentUpgradeUrl,
    currentUpgrade,
  } = useCityBuilder();
  const originalBlocksInCityMetadata = originalBlocksInCity.map(formatMetadata);
  const updatedBlocksInCityMetadata = blocksInCity.map(formatMetadata);

  const allCityMetadata = allCities.map((city) => ({
    cityIndex: city.index,
    cityName: city.params.name,
    blocks: Object.keys(city.metadata.properties)
      .filter((block) => block.includes('#') && !block.includes('Rarity'))
      .map((block) => {
        const value = city.metadata.properties[block];
        return {
          index: parseInt(block.split('#')[1]),
          block: value,
        };
      }),
  }));

  // check in allCityMetadata has a city with the same blocks in it, if so, return that city
  const cityWithSameBlocks = allCityMetadata.find((city) => {
    const cityBlocks = city.blocks;
    const updatedBlocks = updatedBlocksInCityMetadata.filter((city) => city.block);
    const cityBlocksSorted = cityBlocks.sort((a, b) => a.index - b.index);
    const updatedBlocksSorted = updatedBlocks.sort((a, b) => a.index - b.index);
    const theSame = JSON.stringify(cityBlocksSorted) === JSON.stringify(updatedBlocksSorted);
    return theSame;
  });
  const hasBlocks = updatedBlocksInCityMetadata.filter((city) => city.block).length > 0;
  const isDuplicate = !!cityWithSameBlocks && hasBlocks && originalUpgradeUrl === currentUpgradeUrl;

  let loaderLabel = 'A team of Shitty Kitties has started building your Shitty City...';
  if (txnsToConfirm) {
    loaderLabel = 'Please confirm the updates to your Shitty City.';
  }

  return (
    <div className='flex flex-col gap-8'>
      <FullScreenLoaderConfirm
        loading={confirmLoading && !isDuplicate}
        hasClickedConfirm={hasClickedConfirm}
        needToConfirm={txnsToConfirm}
        label={loaderLabel}
      >
        {txnsToConfirm && (
          <div className='flex flex-col gap-5'>
            <p className='max-w-md mx-auto text-center text-lg text-gray-500'>
              Confirming will send your Shitty City Blocks to the Vault and update your Shitty City NFT image and metadata.
            </p>
            {confirmLoading ? (
              <div className='flex justify-center'>
                <Spinner dimensionClasses='w-8 h-8' />
              </div>
            ) : (
              <div className='flex justify-center'>
                <Button loading={confirmLoading} disabled={isDuplicate || !txnsToConfirm} onClick={confirmCity}>
                  Confirm Build
                </Button>
              </div>
            )}
            {!hasClickedConfirm && (
              <Button theme='none' onClick={resetFlow}>
                Cancel
              </Button>
            )}
          </div>
        )}
      </FullScreenLoaderConfirm>

      <div>
        <div>
          {selectedCity && (
            <div className='mt-4 text-center flex justify-center flex-col gap-2'>
              <p className='font-bold text-3xl'>{selectedCity.params.name}</p>
              <p className='text-gray-500'>{selectedCity.index}</p>
            </div>
          )}
        </div>
        <div className='flex justify-center flex-col items-center gap-6 mt-10'>
          <div className='max-w-lg w-full mx-auto flex flex-col gap-2'>
            {!txnsToConfirm && (
              <div className='bg-gray-900 w-full justify-between rounded-lg p-4 flex flex-col md:flex-row gap-3 md:gap-8 md:items-center'>
                <div className='max-w-[250px]'>
                  <p>Initiate your Shitty City Build</p>
                  {currentUpgradeUrl && originalUpgradeUrl !== currentUpgradeUrl ? (
                    <p className='opacity-50'>
                      Upgrade will cost {formatNumber(currentUpgrade.treatsCost)} Treats and {currentUpgrade.algoCost} ALGO.
                    </p>
                  ) : (
                    <p className='opacity-50'>Updating your Shitty City requires two transactions.</p>
                  )}
                </div>

                <div className='w-full md:max-w-[200px]'>
                  <Button className='w-full' loading={confirmLoading} disabled={isDuplicate} onClick={initiateConfirmCity}>
                    Initiate Build
                  </Button>
                </div>
              </div>
            )}
          </div>

          {isDuplicate && cityWithSameBlocks.cityName && (
            <div>
              <Alert type='danger'>
                <span>
                  Cannot confirm updates. This would create a duplicate of{' '}
                  <Link href={`/cities/${cityWithSameBlocks.cityIndex}`}>
                    <a className='font-bold'>{cityWithSameBlocks.cityName}</a>
                  </Link>
                  .
                </span>
              </Alert>
            </div>
          )}
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto w-full'>
        <div className='flex flex-col gap-4'>
          <h2 className='font-bold text-lg text-center'>Original</h2>
          <div className='rounded-lg overflow-hidden bg-gray-900'>
            <CityImage blocks={originalBlocksInCity} cityImageUrl={originalUpgradeUrl} />
          </div>
          <div className='hidden md:grid grid-cols-2 md:grid-cols-3 gap-4'>
            {originalBlocksInCityMetadata.map((mock) => (
              <div className='bg-gray-900 block w-full rounded-lg p-4' key={mock.index}>
                <h3 className='text-gray-500 font-medium text-sm'>Block #{mock.index}</h3>
                <h4 className='mt-2'>{mock.block}</h4>
              </div>
            ))}
          </div>
        </div>
        <div className='flex flex-col gap-4'>
          <h2 className='font-bold text-lg text-center'>Updated</h2>
          <div className='rounded-lg overflow-hidden bg-gray-900'>
            <CityImage blocks={blocksInCity} cityImageUrl={currentUpgradeUrl} />
          </div>
          <div className='hidden md:grid grid-cols-2 md:grid-cols-3 gap-4'>
            {updatedBlocksInCityMetadata.map((mock) => (
              <div className='bg-gray-900 block w-full rounded-lg p-4' key={mock.index}>
                <h3 className='text-gray-600 font-medium text-sm'>Block #{mock.index}</h3>
                <h4 className='mt-2 text-sm'>{mock.block}</h4>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatMetadata = (block) => {
  if (!block.block) {
    return {
      index: block.index,
    };
  }
  return {
    index: block.index,
    block: `${block.block.params.name.split(' - ')?.[1]} - ${block.block.index}`,
  };
};

export default CityBuilderCityConfirm;
